import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHero, StatHero, StatusPill } from "@/components/shared/Visual";
import { Button } from "@/components/ui/button";
import {
  Shield, CheckCircle2, XCircle, Building2, MapPin, CalendarDays,
  Users2, Phone, Clock, ChevronRight, Activity,
} from "lucide-react";

const STATUS_KIND = {
  awaiting_ops_approval: "standby",
  accepted: "active",
  team_being_built: "tracked",
  staff_awaiting_confirmation: "tracked",
  deployment_confirmed: "tracked",
  staff_en_route: "live",
  live: "live",
  completed: "match",
  closed: "neutral",
  declined: "emergency",
};

const STATUS_LABEL = {
  awaiting_ops_approval: "Awaiting Approval",
  accepted: "Accepted",
  team_being_built: "Team Building",
  staff_awaiting_confirmation: "Staff Confirm",
  deployment_confirmed: "Confirmed",
  staff_en_route: "En Route",
  live: "Live",
  completed: "Completed",
  closed: "Closed",
  declined: "Declined",
};

export default function AdminManagedDeployments() {
  const [data, setData] = useState({ deployments: [], counts: {} });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/managed-deployments").then((r) => setData(r.data || { deployments: [], counts: {} })).catch(() => {});
  useEffect(() => { load(); }, []);

  const accept = async (id) => {
    setBusy(true);
    try { await api.post(`/admin/managed-deployments/${id}/accept`); toast.success("Deployment accepted · Deployment Centre unlocked"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const decline = async (id) => {
    const reason = window.prompt("Reason for decline (optional)") || "";
    setBusy(true);
    try { await api.post(`/admin/managed-deployments/${id}/decline`, { reason }); toast.success("Declined"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const c = data.counts || {};
  const pending = data.deployments.filter((d) => d.status === "awaiting_ops_approval");
  const active = data.deployments.filter((d) => ["accepted", "team_being_built", "staff_awaiting_confirmation", "deployment_confirmed", "staff_en_route", "live"].includes(d.status));
  const done = data.deployments.filter((d) => ["completed", "closed", "declined"].includes(d.status));

  return (
    <div className="space-y-6" data-testid="admin-mdr-page">
      <PageHero
        eyebrow="Managed Deployment Requests"
        title="Operations Queue"
        sub={`${c.awaiting_approval || 0} awaiting · ${c.active || 0} active · ${c.completed || 0} completed`}
        right={<StatusPill kind="live">System Operational</StatusPill>}
      />

      <div className="grid sm:grid-cols-3 gap-4">
        <StatHero label="Awaiting Approval" value={c.awaiting_approval || 0} icon={Shield} tone="amber" testId="mdr-stat-awaiting" />
        <StatHero label="Active Deployments" value={c.active || 0} icon={Activity} tone="emerald" testId="mdr-stat-active" />
        <StatHero label="Completed / Closed" value={c.completed || 0} icon={CheckCircle2} tone="brand" testId="mdr-stat-completed" />
      </div>

      {/* Awaiting queue — highest visibility */}
      <div className="space-y-3" data-testid="mdr-awaiting-list">
        <div className="eyebrow eyebrow-brand flex items-center justify-between">
          <span>Awaiting Approval</span>
          <span className="txt-lo">{pending.length}</span>
        </div>
        {pending.length === 0 ? (
          <div className="card-dark p-6 text-center txt-lo font-manrope text-sm">No pending requests.</div>
        ) : pending.map((d) => (
          <div key={d.id} className="card-dark p-5 border-l-4 border-l-amber-400" data-testid={`mdr-row-${d.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="spill spill-tracked"><span className="dot" /> 5% Managed Deployment</span>
                  <StatusPill kind="standby">{STATUS_LABEL[d.status]}</StatusPill>
                </div>
                <div className="font-outfit font-bold txt-hi text-lg mt-2">{d.job_title}</div>
                <div className="grid gap-y-1 gap-x-4 mt-2 sm:grid-cols-2 text-xs txt-md font-manrope">
                  <div className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-brand" /> {d.company_name}</div>
                  <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-brand" /> {d.location}</div>
                  <div className="flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-brand" /> {d.date} · {d.start_time || "—"} – {d.end_time || "—"}</div>
                  <div className="flex items-center gap-1.5"><Users2 className="w-3.5 h-3.5 text-brand" /> {d.workers_needed}× {d.staff_type || "staff"}</div>
                  <div className="flex items-center gap-1.5 col-span-2"><Phone className="w-3.5 h-3.5 text-brand" /> {d.contact_name} · {d.contact_email} · {d.contact_phone || "—"}</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 flex-shrink-0">
                <Button onClick={() => accept(d.id)} disabled={busy} className="btn-brand" data-testid={`mdr-accept-${d.id}`}>
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Accept
                </Button>
                <Button onClick={() => decline(d.id)} disabled={busy} variant="outline" className="btn-ghost-dark hover:text-rose-400" data-testid={`mdr-decline-${d.id}`}>
                  <XCircle className="w-4 h-4 mr-1" /> Decline
                </Button>
                <Link to={`/admin/managed-deployments/${d.id}`} className="btn-ghost-dark">
                  Open <ChevronRight className="w-4 h-4 ml-1" />
                </Link>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Active */}
      {active.length > 0 && (
        <div className="space-y-3" data-testid="mdr-active-list">
          <div className="eyebrow eyebrow-brand">Active</div>
          {active.map((d) => <Row key={d.id} d={d} />)}
        </div>
      )}

      {/* Completed / Closed */}
      {done.length > 0 && (
        <div className="space-y-3">
          <div className="eyebrow eyebrow-brand">Completed &amp; Archive</div>
          {done.map((d) => <Row key={d.id} d={d} />)}
        </div>
      )}
    </div>
  );
}

function Row({ d }) {
  return (
    <Link to={`/admin/managed-deployments/${d.id}`} className="card-dark card-dark-hover p-4 block" data-testid={`mdr-row-${d.id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusPill kind={STATUS_KIND[d.status] || "neutral"}>{STATUS_LABEL[d.status]}</StatusPill>
            <span className="font-outfit font-semibold txt-hi truncate">{d.job_title}</span>
          </div>
          <div className="eyebrow mt-1">{d.company_name} · {d.date} · {d.location}</div>
        </div>
        <ChevronRight className="w-4 h-4 txt-lo" />
      </div>
    </Link>
  );
}
